<script setup lang="ts">
import { ref, onMounted, toRefs, computed, watch } from 'vue';
import { RouterLink, RouterView, useRoute } from 'vue-router';
import { debounce } from '@/utils/index';
const emit = defineEmits([
  'input',
  'blur',
  'change',
  'update:modelValue',
  'search',
]);

const props = defineProps({
  modelValue: [String, Number],
  placeholder: { type: String, default: '请输入内容' },
  showSearchIcon: { type: Boolean, default: false }, //是否显示搜索图标
  width: { type: Number, default: 180 },
  height: { type: Number, default: 28 },
  fontSize: { type: Number, default: 12 },
  maxlength: { type: Number, default: 99999 },
  disabled: { type: Boolean, default: false },
  type: { type: String, default: 'text' },
  isShowFocused: { type: Boolean, default: false }, //聚焦时边框是否变颜色
});

const {
  placeholder,
  showSearchIcon,
  width,
  height,
  fontSize,
  maxlength,
  disabled,
  type,
  isShowFocused,
} = toRefs(props);

const searchValue = computed({
  get: () => props.modelValue,
  set: (value) => emit('update:modelValue', value),
});

const isFocused = ref(false);

// 【修复】中文输入法（IME）组合态标志。
// 原因：input 事件被 debounce 500ms，受控 <input :value="modelValue"> 在 IME 选词、
// 切窗、失焦等导致的 Vue 重渲染时，会把输入框覆盖回 500ms 前的旧值 → 刚输入的内容丢失。
// 解决：① 组合期间不更新受控值；② modelValue 同步与 debounce 解耦，即时同步。
const isComposing = ref(false);

const inputWidth = computed(() => {
  return showSearchIcon.value
    ? Number(width.value) - 48
    : Number(width.value) - 28;
});

const inputStyle = ref({
  width: inputWidth.value + 'px',
  '--placeholder-font-size': fontSize.value + 'px', // 默认 placeholder 字体大小
});

const handleFocus = () => {
  isFocused.value = true;
};
const handleBlur = () => {
  isFocused.value = false;
  emit('blur', searchValue.value);
};
const handleChange = () => {
  emit('change', searchValue.value);
};

const handleCompositionStart = () => {
  isComposing.value = true;
};
const handleCompositionEnd = (e: any) => {
  isComposing.value = false;
  // 组合结束：立即把最终文本同步给受控值，避免被旧值覆盖。
  if (e && e.target) {
    searchValue.value = e.target.value;
  }
};

//搜索框输入
// 【修复】modelValue 的即时同步从 debounce 中拆出：
//   - input 立即更新受控值（保证 DOM 与 state 一致，避免重渲染时被旧值覆盖）；
//   - debounce 仅用于节流向父组件 emit('input') 这类搜索副作用，行为与原先一致。
const emitInputDebounced = debounce((val: any) => {
  emit('input', val);
}, 500);
const hangdleInput = (e: any) => {
  const val = e.target.value;
  // 即时同步受控值（组合态期间由 compositionend 统一处理，这里跳过避免打断 IME）
  if (!isComposing.value) {
    searchValue.value = val;
  }
  emitInputDebounced(val);
};

// 回车搜索
const handleEnter = () => {
  emit('search', searchValue.value);
};

// 点击搜索图标
const handleSearchClick = () => {
  emit('search', searchValue.value);
};
</script>

<template>
  <div
    class="fr-ac-jsb search-box"
    :style="{ width: width + 'px', height: height + 'px' }"
    :class="{
      focused: isFocused && (showSearchIcon || isShowFocused),
      opa4: disabled,
    }"
  >
    <input
      class="custom-input"
      :style="inputStyle"
      :value="searchValue"
      :type="type"
      :placeholder="placeholder"
      :disabled="disabled"
      :maxlength="maxlength"
      @input="hangdleInput"
      @compositionstart="handleCompositionStart"
      @compositionend="handleCompositionEnd"
      @focus="handleFocus"
      @blur="handleBlur"
      @change="handleChange"
      @keyup.enter="handleEnter"
    />
    <img
      class="mr-14 w-12 h-12 ml-8 pointer"
      v-show="showSearchIcon"
      src="@/assets/images/home/icon_search1.png"
      alt=""
      @click="handleSearchClick"
    />
  </div>
</template>

<style lang="scss" scoped>
.search-box {
  height: 28px;
  border-radius: var(--border-radius);
  padding: 0 14px;
  background: var(--input-bg-color);

  .custom-input {
    /* 使用 CSS 变量 */
    font-size: var(--placeholder-font-size);
  }

  .custom-input::placeholder {
    color: var(--input-placeholder-color);
    font-size: var(--placeholder-font-size);
  }
}

.focused::before {
  border-radius: var(--border-radius);
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border: 1px solid transparent;
  background: var(--bg-color-primary);
  /*background: linear-gradient(
      90deg,
      rgba(152, 104, 254, 1),
      rgba(55, 133, 255, 1),
      rgba(40, 207, 181, 1)
    )
    border-box;*/
  mask: linear-gradient(#fff 0 0) padding-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  /* 兼容 WebKit 浏览器 */
  mask-composite: exclude;
  /* 裁剪边框图像 */
  pointer-events: none;
  /* 确保伪元素不影响交互 */
}

@media (max-width: 767px) {
  .search-box {
    width: 100% !important;
    max-width: 100%;
    box-sizing: border-box;
  }

  .search-box .custom-input {
    width: 100% !important;
    min-width: 0;
    flex: 1;
  }
}
</style>
